/**
 * BUGÜN — İLERLEME KAHRAMANI (6.7)
 *
 * ================================================================
 * ÇİZİMDEN ÖLÇÜLDÜ
 * ================================================================
 * `OgrenciBugun.dc.html` → "İLERLEME KAHRAMANI":
 *   blok      marka-600, padding 24px 20px 28px, alt köşeler 24px
 *   karşılama 20px/700 notr-0, letter-spacing -0.01em
 *   tarih     13px notr-0
 *   halka     124×124, r=52, stroke 11, iz beyaz %22, dolgu notr-0,
 *             uçlar yuvarlak, -90° döndürülmüş
 *   sayı      30px/800 notr-0, line-height 1
 *   alt satır 12px notr-0
 *
 * ================================================================
 * SERİ ROZETİ BİLEREK ÇİZİLMEDİ
 * ================================================================
 * Çizimde karşılamanın sağında bir seri rozeti var ("🔥 12 gün").
 * Seri, F-GAM-01'in ("düzenli veri girişi serisi") parçası ve Emre
 * oyun modülünü sona bıraktı — gerekçe `teknik-sartname.md` §4.20'de.
 *
 * Üç seçenek de reddedildi: sabit bir sayı YAZMAK (yalan), istemcide
 * HESAPLAMAK (seri gün bazlı, istemcide yalnızca bugün var — yanlış
 * sonuç verir), boş bir hap ÇİZMEK (gelmemiş bir özelliği varmış gibi
 * göstermek). Rozetin yeri korunuyor: karşılama satırı
 * `justify-between` ile duruyor, rozet geldiğinde düzen değişmeyecek.
 */

import { cn } from "@koclukpanel/ui";

export interface BugunKahramaniProps {
  /** Öğrencinin adı — `GET /ogrenciler/ben` üzerinden geliyor */
  ad: string | null;
  tamamlanan: number;
  toplam: number;
  /** Bugünün görevlerinde çözülen ve hedeflenen soru sayısı */
  cozulenSoru: number;
  hedefSoru: number;
  /** Paydadan düşen mazeretli görev sayısı — F-SCH-09 */
  mazeretli: number;
  /**
   * Görev listesi EN AZ BİR KEZ yüklenebildi mi.
   *
   * ================================================================
   * "ÖLÇÜLMEDİ ≠ SIFIR" — BU BLOK KENDİ KURALINI ÇİĞNİYORDU
   * ================================================================
   * `toplam` iki ayrı sebepten sıfır olabiliyor: gerçekten görev yok,
   * ya da veri hiç gelmedi. İkisi ayırt edilmiyordu ve blok ikisinde
   * de *"Bugün için planlanmış görev yok"* diyordu.
   *
   * Çevrimdışı bir çocuğa bunu demek, yapacak işi olmadığını sanıp
   * uygulamayı kapatmasına yol açıyor. Üretim yapısında canlı
   * ölçüldü (04.09.2026): servis çalışanı kayıtlı, önbellek dolu, ağ
   * yokken ekran yine "0 / 0 görev · Bugün için planlanmış görev yok"
   * diyordu.
   *
   * Aşağıdaki soru sayacı bu kuralı zaten uyguluyordu ("hedefsiz bir
   * günde 0/0 göstermek, ölçülmemiş bir şeyi sıfır gibi sunmak
   * olurdu") — halka ve cümle uygulamıyordu.
   */
  veriGeldi: boolean;
}

/** Çizimdeki biçim: "Salı, 11 Ağustos". */
function bugununAdi(): string {
  const d = new Date();
  const gun = d.toLocaleDateString("tr-TR", { weekday: "long" });
  const kalan = d.toLocaleDateString("tr-TR", { day: "numeric", month: "long" });
  return `${gun.charAt(0).toLocaleUpperCase("tr-TR")}${gun.slice(1)}, ${kalan}`;
}

/**
 * Kalan görev cümlesi.
 *
 * Çizim tek hâli gösteriyor ("İki görev kaldı — bugünü kapatmana az
 * var."). Ekran bütün hâlleri karşılamak zorunda; sayı çiziminkinden,
 * cümle yapısı da çiziminkinden. Sıfır kalan hâli için ayrı bir cümle
 * var çünkü "0 görev kaldı" doğru ama SOĞUK — çizimin bütün fikri
 * "ödül anı".
 */
function kalanCumlesi(
  kalan: number,
  toplam: number,
  mazeretli: number,
  veriGeldi: boolean,
): string {
  // Veri gelmediyse hiçbir OLGU iddia edilmiyor. Sebebi hemen
  // altındaki bağlantı şeridi anlatıyor; burada tekrarlanmıyor.
  if (!veriGeldi) return "Bugünün görevleri henüz yüklenmedi.";

  /*
    MAZERETLİ GÜN "GÖREV YOK" DEĞİLDİR.

    Mazeretli görev F-SCH-09 gereği paydadan düşüyor (`gunluk-ozet.ts`).
    Ama kart listesi onları ÇİZMEYE devam ediyor — mazeret bir silme
    değil, bir işaret. Payda sıfırlanınca ekran "Bugün için planlanmış
    görev yok" diyordu ve hemen altında iki mazeretli kart duruyordu:
    ekran kendi kendini yalanlıyordu.

    Bu cümle mazeretli günü SAYILMAYAN bir gün olarak anlatıyor —
    boş bir gün olarak değil. Sunucudaki `tamamlamaOrani` da aynı ayrımı
    yapıyor: hepsi mazeretliyse oran `null`, sıfır değil.
  */
  if (toplam === 0 && mazeretli > 0) {
    return mazeretli === 1
      ? "Bugünün görevi mazeretli — bu gün sayılmıyor."
      : "Bugünün görevleri mazeretli — bu gün sayılmıyor.";
  }
  if (toplam === 0) return "Bugün için planlanmış görev yok.";
  if (kalan === 0) return "Bugünü kapattın.";
  return `${kalan} görev kaldı — bugünü kapatmana az var.`;
}

const YARICAP = 52;
const CEVRE = 2 * Math.PI * YARICAP; // 326.7 — çizimdeki dasharray

export function BugunKahramani({
  ad,
  tamamlanan,
  toplam,
  cozulenSoru,
  hedefSoru,
  mazeretli,
  veriGeldi,
}: BugunKahramaniProps) {
  const oran = toplam > 0 ? tamamlanan / toplam : 0;
  const soruOrani = hedefSoru > 0 ? Math.min(cozulenSoru / hedefSoru, 1) : 0;

  return (
    <section
      /*
        9b: KENARDAN KENARA BLOK YERİNE YUVARLAK KART (UNİKOÇ kart dili). Degrade
        mor-700 → birincil-bas: iki ucu da beyaz yazıyla AA (6,52 / 4,92). Birincil
        düğmenin pembe ucu (4,35) BİLEREK alınmadı — burada küçük yazı var.
      */
      className="from-mor-700 to-birincil-bas flex flex-col gap-4 rounded-[1.5rem] bg-linear-to-br px-5 pt-6 pb-7 shadow-(--golge-unk-birincil)"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          {/* h1 — UI/UX Dalga 1 (axe G3): ogrenci sekmelerinde sayfa basligi yoktu. Karsilama, Bugun'un basligi. */}
          <h1 className="text-notr-0 text-xl font-bold -tracking-[0.01em]">
            {/*
              Ad GELMEDEN "Merhaba" yazılmıyor: yarım bir karşılama,
              hiç karşılamamaktan daha çok dikkat çeker. Ad yüklenene
              kadar günün kendisi başlık oluyor.
            */}
            {ad ? `Merhaba ${ad}` : "Bugün"}
          </h1>
          <p className="text-notr-0 text-[13px]">{bugununAdi()}</p>
        </div>
        {/* Seri rozetinin yeri — F-GAM'a kadar boş. Yukarıdaki nota bak. */}
      </div>

      <div className="flex items-center gap-5">
        <div className="relative size-[124px] shrink-0">
          <svg
            width="124"
            height="124"
            viewBox="0 0 124 124"
            className="-rotate-90"
            aria-hidden="true"
          >
            {/* İz: beyazın %22'si — koyu marka zemininde halka boşluğu */}
            <circle
              cx="62"
              cy="62"
              r={YARICAP}
              fill="none"
              stroke="rgb(255 255 255 / 0.22)"
              strokeWidth="11"
            />
            <circle
              cx="62"
              cy="62"
              r={YARICAP}
              fill="none"
              stroke="var(--color-notr-0)"
              strokeWidth="11"
              strokeLinecap="round"
              strokeDasharray={CEVRE}
              strokeDashoffset={CEVRE * (1 - oran)}
              className="ease-cikis transition-[stroke-dashoffset] duration-(--sure-yavas)"
            />
          </svg>
          {/*
            SAYI HALKANIN İÇİNDE ve ekran okuyucuya TEK cümle olarak
            veriliyor: "4 / 6 görev" iki ayrı düğüm olarak okunursa
            "dört bölü altı görev" gibi anlamsız çıkıyor.
          */}
          <div
            className="absolute inset-0 flex flex-col items-center justify-center"
            role="img"
            aria-label={
              veriGeldi
                ? `${toplam} görevin ${tamamlanan} tanesi tamamlandı`
                : "Görev sayısı henüz bilinmiyor"
            }
          >
            {/* Veri yokken SIFIR değil TİRE: sıfır bir ölçüm gibi
                okunuyor, tire "henüz bilinmiyor" diyor. Aynı ayrım
                haftalık program şeridinde de var. */}
            <span
              aria-hidden="true"
              className="text-notr-0 text-[30px] leading-none font-extrabold"
            >
              {veriGeldi ? tamamlanan : "—"}
            </span>
            {veriGeldi && (
              <span aria-hidden="true" className="text-notr-0 text-xs">
                / {toplam} görev
              </span>
            )}
          </div>
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-2.5">
          <p className="text-notr-0 text-[15px] leading-[1.35] font-semibold">
            {kalanCumlesi(toplam - tamamlanan, toplam, mazeretli, veriGeldi)}
          </p>

          {/*
            SORU SAYACI yalnızca hedef VARSA çiziliyor. Hedefsiz bir
            günde "0 / 0" göstermek, ölçülmemiş bir şeyi sıfır gibi
            sunmak olurdu — paketin "ölçülmedi ≠ sıfır" kuralı.
          */}
          {hedefSoru > 0 && (
            <div className="flex flex-col gap-1.5">
              <div className="text-notr-0 flex justify-between text-xs">
                <span>Çözülen soru</span>
                <span>
                  {cozulenSoru} / {hedefSoru}
                </span>
              </div>
              <div className="rounded-rozet bg-notr-0/22 h-1.5 overflow-hidden">
                <div
                  className={cn(
                    "rounded-rozet bg-notr-0 h-full",
                    "ease-cikis transition-[width] duration-(--sure-yavas)",
                  )}
                  style={{ width: `${Math.round(soruOrani * 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
